import { useToken } from 'hooks/token'
import { useSeatLocking } from 'hooks/user/useSelling'
import { useCallback, useState } from 'react'
import { Ticket } from 'types/Event'
import { Seat, SeatStatus } from 'types/Location'
import { generateSeatLabelData } from 'utils/seatLabel'
import { getSelectedTicketsFromStorage } from 'utils/sessionStorage'

export const useAuditoriumSeats = (
    seats: Seat[],
    onSelectedSeatsChange: (selectedSeats: string[]) => void,
    tickets: Ticket[],
    eventId: string,
) => {
    const { sessionId } = useToken()
    const seatLocking = useSeatLocking(eventId, sessionId ?? '')

    // Track temporarily selected seats before backend confirms
    const [optimisticSeats, setOptimisticSeats] = useState<string[]>([])

    const getTicketStatusOptimistic = (row: number, column: number): SeatStatus => {
        const foundSeat = seats.find((s) => s.row === row && s.column === column)
        if (!foundSeat) return SeatStatus.NotAvailable

        // If seat is in the optimistic selection, treat it as selected
        if (foundSeat.seat_id && optimisticSeats.includes(foundSeat.seat_id)) {
            return SeatStatus.Selected
        }

        const ticketInfo = tickets.find((ticket) => ticket.seat_id === foundSeat.seat_id)
        if (!ticketInfo) return SeatStatus.NotAvailable

        if (ticketInfo.status === SeatStatus.Reserved) {
            return ticketInfo.locked_by === sessionId ? SeatStatus.Selected : SeatStatus.Reserved
        }

        return ticketInfo.status
    }

    const getTicketPrice = (seatId: string | undefined): number | 'N/A' => {
        if (!seatId) return 'N/A'
        const ticketInfo = tickets?.find((ticket) => ticket.seat_id === seatId)
        return ticketInfo ? ticketInfo.price : 'N/A'
    }

    const handleSeatClick = useCallback(
        async (row: number, column: number) => {
            const selectedTickets = getSelectedTicketsFromStorage(eventId)
            const foundSeat = seats.find((s) => s.row === row && s.column === column)
            const foundTicket = tickets.find((t) => t.seat_id === foundSeat?.seat_id)

            if (!foundSeat || !foundTicket) return

            const isSelected = selectedTickets.includes(foundSeat.seat_id)

            // Optimistically update state
            const updatedSelection = isSelected
                ? selectedTickets.filter((id: string) => id !== foundSeat.seat_id)
                : [...selectedTickets, foundSeat.seat_id]

            setOptimisticSeats(updatedSelection)
            onSelectedSeatsChange(updatedSelection)

            try {
                const success = isSelected
                    ? await seatLocking?.unlockTicketHandler(foundSeat.seat_id!)
                    : await seatLocking?.lockTicketHandler(foundSeat.seat_id!)

                if (!success) {
                    // Revert UI update if backend fails
                    setOptimisticSeats(selectedTickets)
                    onSelectedSeatsChange(selectedTickets)
                }
            } catch (error) {
                console.error('Error handling seat click:', error)
                setOptimisticSeats(selectedTickets) // Revert on error
                onSelectedSeatsChange(selectedTickets)
            }
        },
        [seats, tickets, seatLocking, eventId, onSelectedSeatsChange],
    )

    return {
        getTicketStatusOptimistic,
        getTicketPrice,
        generateSeatLabelData,
        handleSeatClick,
        selectedTickets: getSelectedTicketsFromStorage(eventId),
    }
}
